import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { FileViewerHost } from "@/components/FileViewer";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

function BillingRedirect() {
  const loc = useLocation();
  return <Navigate to={`/admin/billing${loc.search}`} replace />;
}
import KioskHome from "@/pages/KioskHome";
import KioskRouter from "@/pages/KioskRouter";
import CheckInPage from "@/pages/CheckInPage";
import CheckOutPage from "@/pages/CheckOutPage";
import KioskSuccess from "@/pages/KioskSuccess";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import RegisterPage from "@/pages/RegisterPage";
import PortalPage from "@/pages/PortalPage";
import PlatformLayout from "@/pages/platform/PlatformLayout";
import PlatformOverview from "@/pages/platform/PlatformOverview";
import PlatformCompanies from "@/pages/platform/PlatformCompanies";
import PlatformCorporates from "@/pages/platform/PlatformCorporates";
import PlatformPayments from "@/pages/platform/PlatformPayments";
import PlatformAssets from "@/pages/platform/PlatformAssets";
import PlatformCompanyDetail from "@/pages/platform/PlatformCompanyDetail";
import PlatformCorporateDetail from "@/pages/platform/PlatformCorporateDetail";
import PlatformPricing from "@/pages/platform/PlatformPricing";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLog from "@/pages/admin/AdminLog";
import NotesPage from "@/pages/admin/NotesPage";
import DailyLogPage from "@/pages/admin/DailyLogPage";
import RatiosPage from "@/pages/admin/RatiosPage";
import IncidentsPage from "@/pages/admin/IncidentsPage";
import HealthPage from "@/pages/admin/HealthPage";
import TuitionPage from "@/pages/admin/TuitionPage";
import OfficersPage from "@/pages/admin/OfficersPage";
import TimesheetPage from "@/pages/admin/TimesheetPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import BillingPage from "@/pages/admin/BillingPage";
import StudentsPage from "@/pages/admin/StudentsPage";
import StudentProfilePage from "@/pages/admin/StudentProfilePage";
import ClientProfilePage from "@/pages/admin/ClientProfilePage";
import TeacherStudentPage from "@/pages/TeacherStudentPage";
import AttendanceLogPage from "@/pages/admin/AttendanceLogPage";
import IntegrationsPage from "@/pages/admin/IntegrationsPage";
import CalendarPage from "@/pages/admin/CalendarPage";
import NotificationsPage from "@/pages/admin/NotificationsPage";
import EnrollFacePage from "@/pages/EnrollFacePage";
import CorporateLayout from "@/pages/corporate/CorporateLayout";
import CorporateOverview from "@/pages/corporate/CorporateOverview";
import CorporateLocations from "@/pages/corporate/CorporateLocations";
import CorporateBuses from "@/pages/corporate/CorporateBuses";
import CorporateCalendarPage from "@/pages/corporate/CorporateCalendarPage";
import CorporateTransfersPage from "@/pages/corporate/CorporateTransfersPage";
import CorporateBranding from "@/pages/corporate/CorporateBranding";
import CorporateBilling from "@/pages/corporate/CorporateBilling";
import CorporateAnalytics from "@/pages/corporate/CorporateAnalytics";
import CorporateAttendancePage from "@/pages/CorporateAttendancePage";
import CorporateAcademics from "@/pages/corporate/CorporateAcademics";
import TeacherPage from "@/pages/TeacherPage";
import ClassesPage from "@/pages/admin/ClassesPage";
import ReportCardsPage from "@/pages/admin/ReportCardsPage";
import MessagesPage from "@/pages/admin/MessagesPage";
import BusesPage from "@/pages/admin/BusesPage";
import DriverPage from "@/pages/DriverPage";
import OfficerBusStatusPage from "@/pages/OfficerBusStatusPage";
import OfficerFormsPage from "@/pages/OfficerFormsPage";
import KioskClientsPage from "@/pages/KioskClientsPage";
import ClientsPage from "@/pages/admin/ClientsPage";
import StaysPage from "@/pages/admin/StaysPage";
import ParentLogin from "@/pages/parent/ParentLogin";
import ParentInvitePage from "@/pages/parent/ParentInvitePage";
import ParentHome from "@/pages/parent/ParentHome";
import ParentLayout from "@/pages/parent/ParentLayout";
import ParentStudentPage from "@/pages/parent/ParentStudentPage";
import ParentClientPage from "@/pages/parent/ParentClientPage";
import ParentBusPage from "@/pages/parent/ParentBusPage";
import ParentCalendarPage from "@/pages/parent/ParentCalendarPage";
import ParentFormsPage from "@/pages/parent/ParentFormsPage";
import PublicFormPage from "@/pages/parent/PublicFormPage";
import FormsPage from "@/pages/admin/FormsPage";
import CorporateForms from "@/pages/corporate/CorporateForms";
import CorporateFiles from "@/pages/corporate/CorporateFiles";
import FilesPage from "@/pages/admin/FilesPage";
import ParentChat from "@/pages/parent/ParentChat";
import ParentOfficeChat from "@/pages/parent/ParentOfficeChat";
import DismissalPage from "@/pages/DismissalPage";
import DismissalBoardPage from "@/pages/DismissalBoardPage";
import HipaaPage from "@/pages/HipaaPage";
import CompliancePage from "@/pages/admin/CompliancePage";
import "@/App.css";

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/kiosk" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/hipaa" element={<HipaaPage />} />
          <Route path="/enroll-face/:token" element={<EnrollFacePage />} />
          <Route path="/parent/login" element={<ParentLogin />} />
          <Route path="/f/:token" element={<PublicFormPage />} />
          <Route path="/parent/invite/:token" element={<ParentInvitePage />} />
          <Route path="/parent" element={<ParentLayout />}>
            <Route index element={<ParentHome />} />
            <Route path="student/:studentId" element={<ParentStudentPage />} />
            <Route path="client/:clientId" element={<ParentClientPage />} />
            <Route path="bus" element={<ParentBusPage />} />
            <Route path="calendar" element={<ParentCalendarPage />} />
            <Route path="forms" element={<ParentFormsPage />} />
            <Route path="chat/:studentId" element={<ParentChat />} />
            <Route path="office/:companyId" element={<ParentOfficeChat />} />
          </Route>
          <Route path="/dismissal" element={<DismissalPage />} />
          <Route path="/dismissal/board" element={<DismissalBoardPage />} />
          <Route path="/driver" element={<DriverPage />} />
          <Route path="/bus-status" element={<ProtectedRoute><OfficerBusStatusPage /></ProtectedRoute>} />
          <Route path="/forms" element={<ProtectedRoute><OfficerFormsPage /></ProtectedRoute>} />
          <Route path="/kiosk/clients" element={<ProtectedRoute><KioskClientsPage /></ProtectedRoute>} />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <CalendarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher"
            element={
              <ProtectedRoute>
                <TeacherPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/student/:studentId"
            element={
              <ProtectedRoute>
                <TeacherStudentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal"
            element={
              <ProtectedRoute>
                <PortalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/platform"
            element={
              <ProtectedRoute requirePlatformOwner>
                <PlatformLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<PlatformOverview />} />
            <Route path="companies" element={<PlatformCompanies />} />
            <Route path="companies/:id" element={<PlatformCompanyDetail />} />
            <Route path="corporate" element={<PlatformCorporates />} />
            <Route path="corporate/:id" element={<PlatformCorporateDetail />} />
            <Route path="payments" element={<PlatformPayments />} />
            <Route path="assets" element={<PlatformAssets />} />
            <Route path="pricing" element={<PlatformPricing />} />
          </Route>
          <Route
            path="/kiosk"
            element={
              <ProtectedRoute>
                <KioskRouter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kiosk/checkin"
            element={
              <ProtectedRoute>
                <CheckInPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kiosk/checkout"
            element={
              <ProtectedRoute>
                <CheckOutPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kiosk/success"
            element={
              <ProtectedRoute>
                <KioskSuccess />
              </ProtectedRoute>
            }
          />
          <Route path="/billing" element={<BillingRedirect />} />
          <Route
            path="/corporate"
            element={
              <ProtectedRoute requireAdmin>
                <CorporateLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<CorporateOverview />} />
            <Route path="locations" element={<CorporateLocations />} />
            <Route path="buses" element={<CorporateBuses />} />
            <Route path="calendar" element={<CorporateCalendarPage />} />
            <Route path="transfers" element={<CorporateTransfersPage />} />
            <Route path="branding" element={<CorporateBranding />} />
            <Route path="billing" element={<CorporateBilling />} />
            <Route path="analytics" element={<CorporateAnalytics />} />
            <Route path="forms" element={<CorporateForms />} />
            <Route path="files" element={<CorporateFiles />} />
            <Route path="attendance" element={<CorporateAttendancePage />} />
            <Route path="academics" element={<CorporateAcademics />} />
          </Route>
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="log" element={<AdminLog />} />
            <Route path="notes" element={<NotesPage />} />
            <Route path="daily-log" element={<DailyLogPage />} />
            <Route path="ratios" element={<RatiosPage />} />
            <Route path="incidents" element={<IncidentsPage />} />
            <Route path="health" element={<HealthPage />} />
            <Route path="tuition" element={<TuitionPage />} />
            <Route path="files" element={<FilesPage />} />
            <Route path="officers" element={<OfficersPage />} />
            <Route path="timesheet" element={<TimesheetPage />} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="clients/:clientId" element={<ClientProfilePage />} />
            <Route path="stays" element={<StaysPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="students" element={<StudentsPage />} />
            <Route path="students/:studentId" element={<StudentProfilePage />} />
            <Route path="attendance" element={<AttendanceLogPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="forms" element={<FormsPage />} />
            <Route path="classes" element={<ClassesPage />} />
            <Route path="report-cards" element={<ReportCardsPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="buses" element={<BusesPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="compliance" element={<CompliancePage />} />
          </Route>
          <Route path="*" element={<Navigate to="/kiosk" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-center" />
      <FileViewerHost />
    </AuthProvider>
  );
}
